import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import './BaasTransacoes.css';

/**
 * ============================================================================
 * 💳 BaasTransacoes.js — Controle de transações dos cartões corporativos
 * ============================================================================
 * Fonte de verdade: GET /api/baas/account/purchases (CorporatePurchase da Stark
 * Bank). A rota devolve os valores em CENTAVOS — a conversão para reais é feita
 * aqui, no ponto de exibição.
 *
 * O status vem na própria compra (approved/confirmed/denied/canceled/voided).
 * O MOTIVO da recusa NÃO vem na compra: ele vive no CorporatePurchase.Log
 * (campo `errors`). Por isso a gaveta de detalhe busca, sob demanda, os logs em
 * GET /api/baas/account/purchases/:id/logs. Se esse endpoint ainda não estiver
 * publicado no servidor, a tela continua funcionando e apenas informa que o
 * motivo não está disponível — nunca quebra.
 * ============================================================================
 */

// Valores da Stark chegam em centavos.
const toReais = (cents) =>
  (Number.isFinite(Number(cents)) ? Number(cents) : 0) / 100;

const BRL = (cents) =>
  toReais(cents).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Status da CorporatePurchase + os tipos de Log (reversed aparece só no log).
const STATUS = {
  approved:  { label: 'Aprovada',  tone: 'success' },
  confirmed: { label: 'Confirmada', tone: 'success' },
  denied:    { label: 'Negada',    tone: 'danger'  },
  canceled:  { label: 'Cancelada', tone: 'neutral' },
  voided:    { label: 'Anulada',   tone: 'warning' },
  reversed:  { label: 'Revertida', tone: 'warning' },
};
const statusInfo = (s) => STATUS[s] || { label: s || 'Desconhecida', tone: 'neutral' };

// merchantCategoryType → rótulo em PT. Fallback humaniza o valor cru.
const CATEGORIA = {
  fuel: 'Combustível', gasStations: 'Combustível', serviceStations: 'Combustível',
  transport: 'Transporte', transportation: 'Transporte', tolls: 'Pedágio', parking: 'Estacionamento',
  food: 'Alimentação', restaurants: 'Restaurantes', fastFood: 'Alimentação', groceries: 'Mercado',
  supermarkets: 'Mercado', lodging: 'Hospedagem', hotels: 'Hospedagem', travel: 'Viagem',
  airlines: 'Aéreo', health: 'Saúde', pharmacies: 'Farmácia', medical: 'Saúde',
  services: 'Serviços', utilities: 'Utilidades', telecom: 'Telecom', retail: 'Varejo',
  entertainment: 'Entretenimento', education: 'Educação', vehicles: 'Veículos',
  automotive: 'Automotivo', repair: 'Manutenção',
};
const humanize = (v) =>
  String(v || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
const categoriaLabel = (p) =>
  CATEGORIA[p?.merchantCategoryType] || humanize(p?.merchantCategoryType) ||
  (p?.merchantCategoryCode ? `MCC ${p.merchantCategoryCode}` : 'Outros');

const METODO = {
  chip: 'Chip', contactless: 'Aproximação', magstripe: 'Tarja magnética',
  token: 'Carteira digital', server: 'Online', manual: 'Digitado',
};
const metodoLabel = (code) => METODO[code] || (code ? humanize(code) : '—');

const merchantLabel = (p) =>
  p?.merchantDisplayName || p?.merchantName || 'Estabelecimento não informado';

// Data curta e legível. A Stark manda ISO; toleramos ausência.
const fmtData = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const FILTROS_STATUS = [
  { value: '',          label: 'Todos os status' },
  { value: 'approved',  label: 'Aprovadas' },
  { value: 'confirmed', label: 'Confirmadas' },
  { value: 'denied',    label: 'Negadas' },
  { value: 'canceled',  label: 'Canceladas' },
  { value: 'voided',    label: 'Anuladas' },
];

export default function BaasTransacoes() {
  const [rows, setRows]         = useState([]);
  const [cursor, setCursor]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [loadingMore, setMore]  = useState(false);
  const [erro, setErro]         = useState(null);
  const [ambiente, setAmbiente] = useState(null);

  // Filtros: status/datas vão ao servidor; busca é client-side sobre o carregado.
  const [status, setStatus] = useState('');
  const [after, setAfter]   = useState('');
  const [before, setBefore] = useState('');
  const [busca, setBusca]   = useState('');

  // Gaveta de detalhe + cache de logs por compra.
  const [selecionada, setSelecionada] = useState(null);
  const logsCache = useRef(new Map());
  const [logsState, setLogsState] = useState({ loading: false, error: null, logs: null });

  const carregar = useCallback(async ({ append = false, cursorArg = null } = {}) => {
    append ? setMore(true) : setLoading(true);
    if (!append) setErro(null);

    try {
      const params = { limit: 50 };
      if (status) params.status = status;
      if (after)  params.after = after;
      if (before) params.before = before;
      if (cursorArg) params.cursor = cursorArg;

      const { data } = await api.get('/api/baas/account/purchases', { params });
      const novas = Array.isArray(data?.purchases) ? data.purchases : [];
      setAmbiente(data?.ambiente || null);
      setCursor(data?.cursor || null);
      setRows((prev) => (append ? [...prev, ...novas] : novas));
    } catch (e) {
      const msg = e?.response?.data?.error || 'Não foi possível carregar as transações.';
      if (!append) { setRows([]); setCursor(null); }
      setErro(msg);
    } finally {
      append ? setMore(false) : setLoading(false);
    }
  }, [status, after, before]);

  // Recarrega do zero quando um filtro de servidor muda.
  useEffect(() => { carregar({ append: false }); }, [carregar]);

  // ── Busca client-side sobre o que já veio ────────────────────────────────
  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((p) => {
      const alvo = [
        merchantLabel(p), p.holderName, p.cardEnding,
        categoriaLabel(p), p.merchantName,
      ].filter(Boolean).join(' ').toLowerCase();
      return alvo.includes(q);
    });
  }, [rows, busca]);

  // ── KPIs sobre as transações carregadas ──────────────────────────────────
  const kpis = useMemo(() => {
    const base = filtradas;
    const acc = { total: base.length, aprov: 0, aprovValor: 0, neg: 0, negValor: 0 };
    for (const p of base) {
      const v = Number(p.amount) || 0;
      if (p.status === 'approved' || p.status === 'confirmed') { acc.aprov += 1; acc.aprovValor += v; }
      else if (p.status === 'denied') { acc.neg += 1; acc.negValor += v; }
    }
    acc.ticket = acc.aprov ? acc.aprovValor / acc.aprov : 0;
    return acc;
  }, [filtradas]);

  // ── Série diária: aprovadas × negadas por dia (para o gráfico) ───────────
  const porDia = useMemo(() => {
    const mapa = new Map();
    for (const p of filtradas) {
      const d = new Date(p.created);
      if (Number.isNaN(d.getTime())) continue;
      const chave = d.toISOString().slice(0, 10); // YYYY-MM-DD
      const linha = mapa.get(chave) || { dia: chave, aprov: 0, neg: 0 };
      if (p.status === 'approved' || p.status === 'confirmed') linha.aprov += 1;
      else if (p.status === 'denied') linha.neg += 1;
      mapa.set(chave, linha);
    }
    return [...mapa.values()]
      .sort((a, b) => a.dia.localeCompare(b.dia)) // ascendente
      .slice(-14)                                 // últimos 14 dias com dado
      .map((l) => ({ ...l, total: l.aprov + l.neg }));
  }, [filtradas]);

  // ── Logs da compra (motivo da recusa + linha do tempo) ───────────────────
  const abrirDetalhe = useCallback(async (p) => {
    setSelecionada(p);

    if (logsCache.current.has(p.id)) {
      setLogsState({ loading: false, error: null, logs: logsCache.current.get(p.id) });
      return;
    }

    setLogsState({ loading: true, error: null, logs: null });
    try {
      const { data } = await api.get(`/api/baas/account/purchases/${encodeURIComponent(p.id)}/logs`);
      const logs = Array.isArray(data?.logs) ? data.logs : [];
      logsCache.current.set(p.id, logs);
      setLogsState({ loading: false, error: null, logs });
    } catch (e) {
      // 404 = endpoint de logs ainda não publicado. Não é falha da tela.
      const naoPublicado = e?.response?.status === 404;
      setLogsState({
        loading: false,
        logs: null,
        error: naoPublicado
          ? 'O detalhamento de motivo ainda não está disponível neste servidor.'
          : (e?.response?.data?.error || 'Não foi possível carregar o histórico desta transação.'),
      });
    }
  }, []);

  const fecharDetalhe = useCallback(() => {
    setSelecionada(null);
    setLogsState({ loading: false, error: null, logs: null });
  }, []);

  // Fecha a gaveta no ESC.
  useEffect(() => {
    if (!selecionada) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') fecharDetalhe(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selecionada, fecharDetalhe]);

  const exportarCsv = useCallback(() => {
    const head = ['Data', 'Descrição', 'Estabelecimento', 'Titular', 'Final', 'Método', 'Valor (R$)', 'Status'];
    const linhas = filtradas.map((p) => [
      fmtData(p.created), categoriaLabel(p), merchantLabel(p),
      p.holderName || '', p.cardEnding || '', metodoLabel(p.methodCode),
      toReais(p.amount).toFixed(2).replace('.', ','), statusInfo(p.status).label,
    ]);
    const csv = [head, ...linhas]
      .map((l) => l.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transacoes-baas-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filtradas]);

  return (
    <div className="bxt">
      {/* ── Cabeçalho ──────────────────────────────────────────────── */}
      <header className="bxt-head">
        <div>
          <h1 className="bxt-title">Transações</h1>
          <p className="bxt-sub">
            Compras autorizadas e negadas nos cartões corporativos
            {ambiente ? <span className={`bxt-env bxt-env--${ambiente}`}>{ambiente === 'production' ? 'Produção' : 'Sandbox'}</span> : null}
          </p>
        </div>
        <div className="bxt-head-actions">
          <button type="button" className="bxt-btn bxt-btn--ghost" onClick={() => carregar({ append: false })} disabled={loading}>
            {loading ? 'Atualizando…' : 'Atualizar'}
          </button>
          <button type="button" className="bxt-btn" onClick={exportarCsv} disabled={!filtradas.length}>
            Exportar CSV
          </button>
        </div>
      </header>

      {/* ── KPIs ───────────────────────────────────────────────────── */}
      <section className="bxt-kpis" aria-label="Resumo das transações carregadas">
        <div className="bxt-kpi">
          <span className="bxt-kpi-label">Transações</span>
          <span className="bxt-kpi-value">{kpis.total}</span>
        </div>
        <div className="bxt-kpi bxt-kpi--success">
          <span className="bxt-kpi-label">Aprovadas</span>
          <span className="bxt-kpi-value">{kpis.aprov}</span>
          <span className="bxt-kpi-foot">{BRL(kpis.aprovValor)}</span>
        </div>
        <div className="bxt-kpi bxt-kpi--danger">
          <span className="bxt-kpi-label">Negadas</span>
          <span className="bxt-kpi-value">{kpis.neg}</span>
          <span className="bxt-kpi-foot">{BRL(kpis.negValor)}</span>
        </div>
        <div className="bxt-kpi">
          <span className="bxt-kpi-label">Ticket médio aprovado</span>
          <span className="bxt-kpi-value bxt-kpi-value--sm">{BRL(kpis.ticket)}</span>
        </div>
      </section>

      {/* ── Gráfico aprovadas × negadas por dia ────────────────────── */}
      {!loading && !erro && porDia.length > 0 && (
        <GraficoAprovNeg data={porDia} />
      )}

      {/* ── Filtros ────────────────────────────────────────────────── */}
      <section className="bxt-filtros">
        <div className="bxt-field bxt-field--grow">
          <input
            type="search"
            placeholder="Buscar por estabelecimento, titular ou final do cartão"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            aria-label="Buscar transações"
          />
        </div>
        <div className="bxt-field">
          <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filtrar por status">
            {FILTROS_STATUS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="bxt-field">
          <label>De</label>
          <input type="date" value={after} max={before || undefined} onChange={(e) => setAfter(e.target.value)} />
        </div>
        <div className="bxt-field">
          <label>Até</label>
          <input type="date" value={before} min={after || undefined} onChange={(e) => setBefore(e.target.value)} />
        </div>
        {(after || before || status) && (
          <button
            type="button"
            className="bxt-btn bxt-btn--ghost bxt-btn--sm"
            onClick={() => { setStatus(''); setAfter(''); setBefore(''); }}
          >
            Limpar
          </button>
        )}
      </section>

      {/* ── Conteúdo ───────────────────────────────────────────────── */}
      {loading ? (
        <div className="bxt-skeleton" aria-hidden="true">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="bxt-skeleton-row" />)}
        </div>
      ) : erro ? (
        <div className="bxt-state bxt-state--error">
          <p>{erro}</p>
          <button type="button" className="bxt-btn" onClick={() => carregar({ append: false })}>Tentar de novo</button>
        </div>
      ) : filtradas.length === 0 ? (
        <div className="bxt-state">
          <p>
            {rows.length === 0
              ? 'Nenhuma compra foi registrada nos cartões neste período.'
              : 'Nenhuma transação corresponde à busca atual.'}
          </p>
          {busca && <button type="button" className="bxt-btn bxt-btn--ghost" onClick={() => setBusca('')}>Limpar busca</button>}
        </div>
      ) : (
        <>
          <div className="bxt-table" role="table" aria-label="Transações dos cartões">
            <div className="bxt-tr bxt-tr--head" role="row">
              <span role="columnheader">Descrição</span>
              <span role="columnheader">Titular do cartão</span>
              <span role="columnheader">Método</span>
              <span role="columnheader" className="bxt-col-right">Valor</span>
              <span role="columnheader" className="bxt-col-center">Status</span>
            </div>

            {filtradas.map((p) => {
              const s = statusInfo(p.status);
              return (
                <button
                  key={p.id}
                  type="button"
                  className="bxt-tr bxt-tr--row"
                  role="row"
                  onClick={() => abrirDetalhe(p)}
                >
                  <span className="bxt-cell-desc" role="cell">
                    <span className={`bxt-mcc bxt-mcc--${s.tone}`} aria-hidden="true">
                      {categoriaLabel(p).charAt(0)}
                    </span>
                    <span className="bxt-desc-txt">
                      <small>{categoriaLabel(p)}</small>
                      <strong title={merchantLabel(p)}>{merchantLabel(p)}</strong>
                      <time>{fmtData(p.created)}</time>
                    </span>
                  </span>

                  <span className="bxt-cell-holder" role="cell">
                    <strong>{p.holderName || '—'}</strong>
                    {p.cardEnding ? <span className="bxt-ending">•••• {p.cardEnding}</span> : null}
                  </span>

                  <span className="bxt-cell-method" role="cell">{metodoLabel(p.methodCode)}</span>

                  <span className="bxt-cell-value bxt-col-right" role="cell">{BRL(p.amount)}</span>

                  <span className="bxt-col-center" role="cell">
                    <span className={`bxt-pill bxt-pill--${s.tone}`}>{s.label}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {cursor && (
            <div className="bxt-more">
              <button type="button" className="bxt-btn bxt-btn--ghost" onClick={() => carregar({ append: true, cursorArg: cursor })} disabled={loadingMore}>
                {loadingMore ? 'Carregando…' : 'Carregar mais'}
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Gaveta de detalhe ──────────────────────────────────────── */}
      {selecionada && (
        <DetalheTransacao
          p={selecionada}
          logsState={logsState}
          onClose={fecharDetalhe}
        />
      )}
    </div>
  );
}

// ============================================================================
// Gráfico: aprovadas × negadas por dia (barras empilhadas, sem libs externas)
// ============================================================================
function GraficoAprovNeg({ data }) {
  const maxTotal = Math.max(1, ...data.map((d) => d.total));
  const totAprov = data.reduce((s, d) => s + d.aprov, 0);
  const totNeg   = data.reduce((s, d) => s + d.neg, 0);

  const rotuloDia = (iso) => {
    const [, m, dd] = iso.split('-');
    return `${dd}/${m}`;
  };

  return (
    <section className="bxt-chart" aria-label="Transações aprovadas e negadas por dia">
      <div className="bxt-chart-head">
        <span className="bxt-chart-title">Aprovadas × negadas por dia</span>
        <div className="bxt-chart-legend">
          <span><i className="bxt-dot bxt-dot--success" />Aprovadas ({totAprov})</span>
          <span><i className="bxt-dot bxt-dot--danger" />Negadas ({totNeg})</span>
        </div>
      </div>

      <div className="bxt-chart-bars" role="img"
        aria-label={`Aprovadas: ${totAprov}. Negadas: ${totNeg}. Distribuídas em ${data.length} dia(s).`}>
        {data.map((d) => {
          const hAprov = (d.aprov / maxTotal) * 100;
          const hNeg   = (d.neg / maxTotal) * 100;
          return (
            <div className="bxt-bar-col" key={d.dia}>
              <div className="bxt-bar-track" title={`${rotuloDia(d.dia)} — ${d.aprov} aprovada(s), ${d.neg} negada(s)`}>
                {d.neg > 0 && (
                  <div className="bxt-bar-seg bxt-bar-seg--neg" style={{ height: `${hNeg}%` }} />
                )}
                {d.aprov > 0 && (
                  <div className="bxt-bar-seg bxt-bar-seg--aprov" style={{ height: `${hAprov}%` }} />
                )}
                {d.total === 0 && <div className="bxt-bar-seg bxt-bar-seg--vazio" />}
              </div>
              <span className="bxt-bar-day">{rotuloDia(d.dia)}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ============================================================================
// Gaveta lateral: detalhe da transação + motivo/linha do tempo (logs)
// ============================================================================
function DetalheTransacao({ p, logsState, onClose }) {
  const s = statusInfo(p.status);

  // O motivo mora nos `errors` do log. Reunimos os erros de todos os eventos.
  const motivos = useMemo(() => {
    const set = new Set();
    (logsState.logs || []).forEach((l) => (l.errors || []).forEach((e) => e && set.add(String(e))));
    return [...set];
  }, [logsState.logs]);

  const timeline = useMemo(
    () => [...(logsState.logs || [])].sort((a, b) => new Date(b.created) - new Date(a.created)),
    [logsState.logs]
  );

  const negada = p.status === 'denied';

  return (
    <div className="bxt-drawer-overlay" onClick={onClose}>
      <aside className="bxt-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Detalhe da transação">
        <header className="bxt-drawer-head">
          <div>
            <span className={`bxt-pill bxt-pill--${s.tone}`}>{s.label}</span>
            <h2>{merchantLabel(p)}</h2>
            <p>{categoriaLabel(p)} · {fmtData(p.created)}</p>
          </div>
          <button type="button" className="bxt-drawer-close" onClick={onClose} aria-label="Fechar">✕</button>
        </header>

        <div className="bxt-drawer-amount">
          <span>Valor</span>
          <strong>{BRL(p.amount)}</strong>
        </div>

        {/* Motivo em destaque quando negada */}
        {negada && (
          <div className="bxt-motivo">
            <span className="bxt-motivo-titulo">Por que foi negada</span>
            {logsState.loading ? (
              <p className="bxt-motivo-txt bxt-muted">Buscando o motivo na Stark Bank…</p>
            ) : logsState.error ? (
              <p className="bxt-motivo-txt bxt-muted">{logsState.error}</p>
            ) : motivos.length ? (
              <ul className="bxt-motivo-lista">
                {motivos.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            ) : (
              <p className="bxt-motivo-txt bxt-muted">A Stark Bank não retornou um motivo detalhado para esta recusa.</p>
            )}
          </div>
        )}

        {/* Campos da compra */}
        <dl className="bxt-detalhes">
          <div><dt>Titular</dt><dd>{p.holderName || '—'}</dd></div>
          <div><dt>Final do cartão</dt><dd>{p.cardEnding ? `•••• ${p.cardEnding}` : '—'}</dd></div>
          <div><dt>Método</dt><dd>{metodoLabel(p.methodCode)}</dd></div>
          <div><dt>Categoria</dt><dd>{categoriaLabel(p)}</dd></div>
          <div><dt>País</dt><dd>{p.merchantCountryCode || '—'}</dd></div>
          {Number(p.tax) > 0 && <div><dt>Taxa</dt><dd>{BRL(p.tax)}</dd></div>}
          {Number(p.merchantAmount) > 0 && Number(p.merchantAmount) !== Number(p.amount) && (
            <div><dt>Valor no estabelecimento</dt><dd>{BRL(p.merchantAmount)}</dd></div>
          )}
          {Array.isArray(p.tags) && p.tags.length > 0 && (
            <div className="bxt-detalhe-wide"><dt>Tags</dt><dd>{p.tags.join(', ')}</dd></div>
          )}
          <div className="bxt-detalhe-wide"><dt>ID da compra</dt><dd className="bxt-mono">{p.id}</dd></div>
        </dl>

        {/* Linha do tempo do ciclo de vida (logs) */}
        <div className="bxt-timeline-wrap">
          <span className="bxt-motivo-titulo">Histórico</span>
          {logsState.loading ? (
            <p className="bxt-muted">Carregando histórico…</p>
          ) : logsState.error ? (
            <p className="bxt-muted">{logsState.error}</p>
          ) : timeline.length ? (
            <ol className="bxt-timeline">
              {timeline.map((l) => {
                const li = statusInfo(l.type);
                return (
                  <li key={l.id || l.created} className={`bxt-tl-item bxt-tl-item--${li.tone}`}>
                    <span className="bxt-tl-dot" aria-hidden="true" />
                    <div>
                      <strong>{li.label}</strong>
                      <time>{fmtData(l.created)}</time>
                      {Array.isArray(l.errors) && l.errors.length > 0 && (
                        <span className="bxt-tl-err">{l.errors.join(' · ')}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="bxt-muted">Sem histórico adicional para esta transação.</p>
          )}
        </div>
      </aside>
    </div>
  );
}